import { useForm } from 'antd/es/form/Form';
import { useCallback, useContext, useState } from 'react';

import {
  useGetBranchIndicatorsMutation,
  type ModelBranchIndicatorsReport,
} from '@app/api/Analytics';
import { useUserPrint } from '@app/hooks';
import { DEFAULT_FILTERS_DATA } from '@app/pages/Analytics/data';
import type { FiltersInterface } from '@app/pages/Analytics/ExpenseByArticlesReport/types';
import type { HandleSearchDataProps } from '@app/pages/Analytics/types';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import { useRegistryFilterState } from '@app/shared/hooks';

import type { BaseOptionType } from 'antd/es/select';

export const useBranchIndicatorsReport = () => {
  // Выбранные филиалы переживают уход на другой раздел бокового меню и
  // возврат обратно (issue/11161).
  const [selectedBranches, setSelectedBranches] = useRegistryFilterState<
    BaseOptionType[] | null
  >('analytics', 'branch-indicators:selectedBranches', null);
  const [allBranches, setAllBranches] = useState<BaseOptionType[]>([]);
  const [tableData, setTableData] = useState<ModelBranchIndicatorsReport>();

  const [form] = useForm<FiltersInterface>();

  const { activeBranchId: ActiveBranchId } = useUserPrint();

  const { showNotification } = useContext(NotificationContext);

  const [getBranchIndicators, { isLoading: isGetBranchIndicatorsLoading }] =
    useGetBranchIndicatorsMutation();

  const handleSearchData = useCallback(
    async ({
      filterMonth,
      filterYear,
      branchIds,
    }: HandleSearchDataProps & { branchIds: number[] | null }) => {
      try {
        const result = await getBranchIndicators({
          ActiveBranchId,
          data: {
            Year: filterYear,
            Month: filterMonth,
            BranchIds: branchIds,
          },
        }).unwrap();

        setTableData(result);
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    },
    [ActiveBranchId, getBranchIndicators, showNotification]
  );

  const handleReset = useCallback(() => {
    form.resetFields();

    handleSearchData({
      ...DEFAULT_FILTERS_DATA,
      branchIds: null,
    });
    setSelectedBranches(null);
  }, [form, handleSearchData, setSelectedBranches]);

  const handleSubmitForm = useCallback(
    (values: FiltersInterface) => {
      const { filterMonth, filterYear } = values;

      handleSearchData({
        filterMonth,
        filterYear,
        branchIds: selectedBranches?.map(({ value }) => value) || [],
      });
    },
    [handleSearchData, selectedBranches]
  );

  return {
    handleReset,
    form,
    handleSubmitForm,
    isGetBranchIndicatorsLoading,
    allBranches,
    setAllBranches,
    tableData,
    selectedBranches,
    setSelectedBranches,
    handleSearchData,
  };
};
